import { Box, Skeleton, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MonitoredServersList,
  ServerEvent,
  ServerEventsList,
} from "@xop/dashboard-shared";
import { useEffect, useRef } from "react";

import { Pagination } from "../../components/ui/pagination.js";
import { useServerPagination } from "../../hooks/useServerPagination.js";
import { api } from "../../lib/api.js";
import { MONITORED_KEY } from "./ServerList.js";

/**
 * Right-hand history panel on the Server Status page. Reads the
 * paginated `/api/servers/events` endpoint — only state transitions are
 * stored, so this is the "what actually happened over the last N days"
 * timeline, not a noisy per-probe log.
 *
 * Layout note: the parent page allocates ~30% of the page width to this
 * panel via a CSS grid; we own everything inside that column. Default
 * lookback is 20 days, per the product brief.
 */

const DAYS = 20;

export const SERVER_EVENTS_KEY = ["servers", "events"] as const;

export function ServerEventHistory(): JSX.Element {
  const { page, perPage, setPage, setPerPage } = useServerPagination({
    defaultPerPage: 25,
    pageParam: "events_page",
    perPageParam: "events_perPage",
  });
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [...SERVER_EVENTS_KEY, page, perPage] as const,
    queryFn: () =>
      api<ServerEventsList>(
        `/api/servers/events?page=${page}&perPage=${perPage}&days=${DAYS}`,
        { retryOnAuthError: true },
      ),
    // Match the status cards' poll cadence so the panel can never lag
    // behind the visual state of the servers it describes.
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    placeholderData: (prev) => prev,
  });

  // Mirror live state from the monitored-servers query. ServerList owns
  // that query; we piggy-back on its cache instead of double-polling.
  // Whenever a server's reachable flag flips between renders, the
  // background sweep has just written a `server_events` row — invalidate
  // *now* so the panel updates within the same poll cycle instead of
  // waiting for the next 15-second tick.
  const monitored = useQuery<MonitoredServersList>({
    queryKey: MONITORED_KEY,
    enabled: false,
    // ServerList provides the data; we only subscribe to the cache.
  });
  const prevReachableRef = useRef<Map<number, boolean | null> | null>(null);
  useEffect(() => {
    const servers = monitored.data?.servers;
    if (!servers) return;
    const next = new Map<number, boolean | null>(
      servers.map((s) => [s.id, s.reachable]),
    );
    const prev = prevReachableRef.current;
    prevReachableRef.current = next;
    if (!prev) return; // first sweep — no baseline yet, nothing flipped

    let flipped = false;
    for (const [id, r] of next) {
      if (!prev.has(id)) continue;
      const before = prev.get(id);
      // null→null doesn't matter. true→false or false→true does, and
      // null→true|false also counts (the very first probe is itself a
      // signal worth refreshing on).
      if (before !== r) {
        flipped = true;
        break;
      }
    }
    if (flipped) {
      qc.invalidateQueries({ queryKey: SERVER_EVENTS_KEY });
    }
  }, [monitored.data, qc]);

  return (
    <Box
      component="aside"
      aria-label="Server status history"
      sx={{
        display: "grid",
        height: "100%",
        alignContent: "start",
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
      }}
    >
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: 1,
          borderColor: "divider",
          px: 2,
          py: 1.5,
        }}
      >
        <Box>
          <Typography component="h2" sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}>
            History
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
            Last {DAYS} days · up/down transitions only
          </Typography>
        </Box>
        {data && (
          <Box component="span" sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
            {data.total} event{data.total === 1 ? "" : "s"}
          </Box>
        )}
      </Box>

      <Box sx={{ display: "grid", gap: 0.75, px: 1, pb: 1 }}>
        {isLoading && (
          <Box sx={{ display: "grid", gap: 1, px: 0.5, py: 0.5 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" sx={{ height: 48 }} />
            ))}
          </Box>
        )}
        {isError && (
          <Typography sx={{ px: 0.5, fontSize: 12, color: "error.main" }}>
            {error instanceof Error ? error.message : "Could not load history"}
          </Typography>
        )}
        {data && data.events.length === 0 && !isLoading && (
          <Typography sx={{ px: 0.5, py: 3, textAlign: "center", fontSize: 12, color: "text.disabled" }}>
            No transitions in the last {DAYS} days.
            <br />
            All monitored servers have been stable.
          </Typography>
        )}

        {data && data.events.length > 0 && (
          <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {data.events.map((ev) => (
              <EventRow key={ev.id} event={ev} />
            ))}
          </Box>
        )}

        {data && (
          <Box sx={{ borderTop: 1, borderColor: "divider", px: 1, pt: 1 }}>
            <Pagination
              page={page}
              perPage={perPage}
              totalCount={data.total}
              totalPages={Math.max(1, Math.ceil(data.total / Math.max(1, perPage)))}
              entityNoun="events"
              hideIfSinglePage
              onPageChange={setPage}
              onPerPageChange={setPerPage}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function EventRow({ event }: { event: ServerEvent }): JSX.Element {
  const down = event.event_type === "down";
  const detail = down
    ? event.error ?? "down"
    : event.http_status != null
      ? `HTTP ${event.http_status}${event.latency_ms != null ? ` · ${event.latency_ms} ms` : ""}`
      : "recovered";

  return (
    <Box
      component="li"
      data-testid={`history-event-${event.id}`}
      data-event-type={event.event_type}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        borderBottom: 1,
        borderColor: "divider",
        px: 1,
        py: 0.75,
        "&:last-child": { borderBottom: 0 },
        "&:hover": { bgcolor: "surfaceSoft" },
      }}
      title={event.server_url ?? undefined}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          height: 6,
          width: 6,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: down ? "error.main" : "success.main",
        }}
      />
      <Box component="span" sx={{ position: "absolute", width: 1, height: 1, p: 0, m: -0.125, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 }}>
        {down ? "Down" : "Recovered"}:{" "}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1, lineHeight: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, fontWeight: 500, color: "text.primary" }}>
            {event.server_label ?? <em>(removed)</em>}
          </Box>
          <Box
            component="time"
            dateTime={event.occurred_at}
            sx={{ flexShrink: 0, fontSize: 10, fontVariantNumeric: "tabular-nums", color: "text.disabled" }}
          >
            {formatTime(event.occurred_at)}
          </Box>
        </Box>
        <Typography
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: 10,
            color: down ? "error.main" : "text.disabled",
          }}
        >
          {detail}
        </Typography>
      </Box>
    </Box>
  );
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
